import {RouteComponentProps, useParams} from "react-router";
import React, {Fragment, useEffect, useState} from "react";
import PageHeaderLayout from "../../layout/pageHeaderLayout";
import {useForm, useWatch} from "antd/lib/form/Form";
import {Button, Card, Col, Form, Input, InputNumber, message, Row, Select as AntSelect, Space, Table} from "antd";
import Select from "../../components/Select";
import * as dealerApi from "../../apis/dealer";
import * as api from "../../apis/rebate";
import * as categoryApi from '../../apis/productCategory';
import * as contractApi from '../../apis/platforms/admin/contract';

import {RebateBusinessScopeEnum, RebateBusinessScopeOption, RebateStatusEnum} from "../../utils/constants";
import DatePicker from "../../components/DatePicker";
import styles from "./index.module.css";
import DictSelect from "../../components/DictSelect";
import {useRequest} from "ahooks";
import {useHistory} from "react-router-dom";
import dayjs from "dayjs";
import {ColumnProps} from "antd/lib/table";
import ProductSelectModal from "../../components/ProductSelectModal";
import {StandardColumnsType} from "../../components/StandardTable";
import withAuth from "../../utils/withAuth";
import Divider from "../../components/Divider";
import UploadModal from "../../components/UploadModal";


type RouteParams = {
    id?: string
}

enum Mode {
    Create,
    Detail,
    Edit
}

enum StatusMode {
    Disabled,
    EnableModifyAll,
    EnableModifyDate,
}

const AuditFragment = withAuth('audit', Fragment);
const EditFragment = withAuth('edit', Fragment);
const EditDateFragment = withAuth('editDate', Fragment);
const CloseFragment = withAuth('close', Fragment);
const DeleteFragment = withAuth('delete', Fragment);


function RebateDetail(props: RouteComponentProps<RouteParams>) {
    const id = useParams<RouteParams>().id;
    const history = useHistory();
    const [mode, setMode] = useState<Mode>(!!id ? Mode.Detail : Mode.Edit);
    const [statusMode, setStatusMode] = useState<StatusMode>();
    const [form] = useForm();
    const auditorName = useWatch('auditorName', form);
    const auditedAt = useWatch('auditedAt', form);
    const status = useWatch('status', form);
    const remainingAmount = useWatch('remainingAmount', form);
    const businessScope = useWatch('businessScope', form);
    const contractId = useWatch('contractId', form);
    const products: Array<any> = useWatch('products', form) || [];
    const categories: Array<any> = useWatch('categories', form) || [];
    const dealerId = useWatch('dealerId', form);
    const [productSelectedModalVisible, setProductSelectedModalVisible] = useState(false);
    const [categorySelectedModalVisible, setCategorySelectedModalVisible] = useState(false);

    const [uploadModalVisible, setUploadModalVisible] = useState(false)

    const {run: doImport, loading: doImportLoading} = useRequest(api.importRebateOrderProducts, {
        manual: true,
        onSuccess: () => {
            window.location.reload();
        }
    })

    useEffect(() => {
        if (!!id) {
            runGetData(parseInt(id));
            setMode(Mode.Detail);
        } else {
            setMode(Mode.Create);
        }
    }, [id]);
    useEffect(() => {
        if (mode === Mode.Detail) {
            if (businessScope == RebateBusinessScopeEnum.Product) {
                const productsList = (form.getFieldValue('rebateOrderProducts') || []).map((p: any) => {
                    if (p.objectId) {
                        p.id = p.objectId;
                    }
                    return {...p};
                });
                form.setFieldValue('products', productsList);
            } else if (businessScope == RebateBusinessScopeEnum.Category) {
                const categoryList = (form.getFieldValue('rebateOrderProducts') || []).map((c: any) => {
                    if (c.objectId) {
                        c.id = c.objectId;
                    }
                    return {...c};
                });
                form.setFieldValue('categories', categoryList);
            }
        }
    }, [businessScope]);

    useEffect(() => {
        if (mode == Mode.Create) {
            setStatusMode(StatusMode.EnableModifyAll);
        } else if (mode == Mode.Edit && status == RebateStatusEnum.Unapproved) {
            setStatusMode(StatusMode.EnableModifyAll);
        } else if (mode == Mode.Edit && (status == RebateStatusEnum.Enable || (status == RebateStatusEnum.Closed && remainingAmount > 0))) {
            setStatusMode(StatusMode.EnableModifyDate);
        } else {
            setStatusMode(StatusMode.Disabled);
        }
    }, [mode, status, remainingAmount]);

    const {loading: loadingGetData, run: runGetData} = useRequest(api.getRebate, {
        manual: true,
        onSuccess(res) {
            form.setFieldsValue(res?.data?.data || {});
        }
    });

    const {loading: loadingCreate, run: runCreate} = useRequest(api.AddRebate, {
        manual: true,
        onSuccess(res) {
            const newRebateId = res.data.data;
            message.success('添加返利成功');
            history.replace(`/rebate/rebate/detail/${newRebateId}`);
        }
    });

    const {loading: loadingUpdate, run: runUpdate} = useRequest(api.updateRebate, {
        manual: true,
        onSuccess(res) {
            message.success('修改返利成功');
            if (!!id) {
                runGetData(parseInt(id));
            }
            setMode(Mode.Detail);
        }
    })

    const {loading: loadingApprove, run: runApprove} = useRequest(api.approveRebate, {
        manual: true,
        onSuccess(res) {
            if (!!id) {
                runGetData(parseInt(id));
            }
        }
    });

    const {loading: loadingClose, run: runClose} = useRequest(api.closeRebate, {
        manual: true,
        onSuccess(res) {
            message.success('已关闭');
            history.replace('/rebate/rebate');
        }
    });


    const handleSave = () => {
        form.validateFields().then(value => {
            if (value.businessScope == RebateBusinessScopeEnum.Product) {
                value.rebateOrderProductIds = products.map(p => p.id);
            } else if (value.businessScope == RebateBusinessScopeEnum.Category) {
                value.rebateOrderProductIds = categories.map(c => c.id);
            }
            if (mode == Mode.Create) {
                runCreate(value);
            } else if (mode == Mode.Edit) {
                runUpdate(value);
            }
        });
    }

    const handleClose = () => {
        if (!!id) {
            runClose(parseInt(id));
        }
    }
    const handleCancel = () => {
        if (mode == Mode.Edit) {
            setMode(Mode.Detail);
            return;
        }
        history.goBack();
    }

    const handleDealerChanged = () => {
        if (contractId) {
            form.setFieldValue('contractId', null);
        }
    }

    const handleCategoriesSelected = (keys?: Array<number>, records?: Array<any>) => {
        if (records?.some(c => categories.some(cc => cc.code == c.code))) {
            message.error('产品线已存在');
        } else {
            const result = records?.map(c => ({id: c.id, code: c.code, name: c.name})) || [];
            form.setFieldValue('categories', [...categories, ...result]);
        }
        setCategorySelectedModalVisible(false);
    }
    const handleCategoryAdd = () => {
        setCategorySelectedModalVisible(true);
    }
    const handleCategoryRemove = (index: number) => {
        form.setFieldValue('categories', categories.filter((_, i) => i != index));
    }

    const handleProductSelected = (keys?: Array<number>, records?: Array<any>) => {
        if (records?.some(p => products.some(pp => pp.code == p.code))) {
            message.error('商品已存在');
        } else {
            const result = records?.map(p => ({
                id: p.id,
                code: p.code,
                specification: p.specification,
                name: p.name
            })) || [];
            form.setFieldValue('products', [...products, ...result]);
        }
        setProductSelectedModalVisible(false);
    }
    const handleProductAdd = () => {
        setProductSelectedModalVisible(true);
    }
    const handleProductRemove = (index: number) => {
        form.setFieldValue('products', products.filter((_, i) => i != index));
    }

    const productColumns: Array<ColumnProps<any>> = [
        {
            dataIndex: 'operation',
            key: 'operation',
            title: '操作',
            width: 200,
            render(v, r, i) {
                return <Space>
                    <Button onClick={() => handleProductRemove(i)}>-</Button>
                    <Button onClick={handleProductAdd}>+</Button>
                </Space>
            }
        },
        {
            dataIndex: 'code',
            key: 'code',
            title: '商品编号'
        },
        {
            dataIndex: 'name',
            key: 'name',
            title: '商品名称'
        },
        {
            dataIndex: 'specification',
            key: 'specification',
            title: '规格型号'
        }
    ];
    const categoryColumns: Array<ColumnProps<any>> = [
        {
            dataIndex: 'operation',
            key: 'operation',
            title: '操作',
            width: 200,
            render(v, r, i) {
                return <Space>
                    <Button onClick={() => handleCategoryRemove(i)}>-</Button>
                    <Button onClick={handleCategoryAdd}>+</Button>
                </Space>
            }
        },
        {
            dataIndex: 'code',
            key: 'code',
            title: '产品线编号'
        },
        {
            dataIndex: 'name',
            key: 'name',
            title: '产品线名称'
        }
    ];
    const categorySelectColumns: StandardColumnsType = categoryColumns.slice(1) as StandardColumnsType;

    const header = (
        <div className={styles.Toolbar}>
            <Space>
                <span>审核人：{auditorName}</span>
                <span>审核时间：{auditedAt && dayjs(auditedAt).format('ll')}</span>
            </Space>
            <Divider>
                {
                    mode !== Mode.Edit && <>
                        {status == RebateStatusEnum.Unapproved &&
                            <AuditFragment><Button type={'link'} loading={loadingApprove}
                                                   onClick={() => runApprove(parseInt(id!!))}>审核</Button></AuditFragment>}
                        {status == RebateStatusEnum.Unapproved &&
                            <EditFragment><Button type={'link'} loading={loadingUpdate}
                                                  onClick={() => setMode(Mode.Edit)}>修改</Button></EditFragment>}
                        {(status == RebateStatusEnum.Enable || (status == RebateStatusEnum.Closed && remainingAmount > 0)) &&
                            <EditDateFragment><Button type={'link'} loading={loadingUpdate}
                                                      onClick={() => setMode(Mode.Edit)}>修改日期</Button></EditDateFragment>}
                        {status == RebateStatusEnum.Unapproved &&
                            <DeleteFragment><Button type={'link'} loading={loadingClose} onClick={handleClose}>删除</Button></DeleteFragment>}
                        {(status == RebateStatusEnum.Enable || status == RebateStatusEnum.Approved) &&
                            <CloseFragment><Button type={"link"} loading={loadingClose} onClick={handleClose}>关闭</Button></CloseFragment>}
                        {(status == RebateStatusEnum.Unapproved && (businessScope === RebateBusinessScopeEnum.Product || businessScope === RebateBusinessScopeEnum.Category)) &&
                            <CloseFragment><Button type={"link"}
                                                   onClick={() => setUploadModalVisible(true)}>导入</Button></CloseFragment>}
                    </>
                }
                <Button type={"link"} onClick={handleCancel}>返回</Button>
            </Divider>
        </div>
    )

    return <PageHeaderLayout withFooterBar={mode != Mode.Detail} onOk={handleSave} onCancel={handleCancel}>
        <Card title={mode != Mode.Create && header}>
            <Form form={form} labelCol={{span: 6}}
                  disabled={statusMode !== StatusMode.EnableModifyAll}>
                <Form.Item name={'id'} noStyle/>
                <Form.Item name={'auditorName'} noStyle/>
                <Form.Item name={'auditedAt'} noStyle/>
                <Form.Item name={'status'} noStyle/>
                <Form.Item name={'remainingAmount'} noStyle/>
                <h3 className={styles.SectionTitle}>基本信息</h3>
                <Row>
                    <Col span={8} key={'code'}>
                        <Form.Item label={'返利单号'}>
                            <Input disabled value={mode == Mode.Create ? '自动生成' : form.getFieldValue('code')}/>
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'dealer'}>
                        <Form.Item name={'dealerId'} label={'客户'} rules={[{required: true}]}>
                            <Select url={dealerApi.getConfirmedDealerList} getData={data => data.data}
                                    textField={'name'} onChange={handleDealerChanged}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'rebateType'}>
                        <Form.Item name={'rebateType'} label={'返利类型'} rules={[{required: true}]}>
                            <DictSelect dict={'RebateType'} appendNull={false}/>
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'businessScope'}>
                        <Form.Item name={'businessScope'} label={'业务范围'} initialValue={0}
                                   rules={[{required: true}]}>
                            <AntSelect options={RebateBusinessScopeOption}
                                       fieldNames={{value: 'value', label: 'text'}}
                                       onChange={value => {
                                           if (value !== 1) form.setFieldValue('categories', undefined)
                                           if (value !== 2) form.setFieldValue('products', undefined)
                                           if (value !== 3) form.setFieldValue('contractId', undefined)
                                       }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'rebateAmount'}>
                        <Form.Item name={'rebateAmount'} label={'返利额度'} rules={[{required: true}]}>
                            <InputNumber step={0.01} style={{width: '100%'}}/>
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'currency'}>
                        <Form.Item name={'currency'} label={'币种'} initialValue={'CNY'}>
                            <AntSelect options={[{value: 'CNY', text: 'CNY'}]}/>
                        </Form.Item>
                    </Col>
                    {
                        businessScope == RebateBusinessScopeEnum.Contract ? (
                            <Col span={8} key={'contractId'}>
                                <Form.Item name={'contractId'} label={'合同'} rules={[{required: true}]}>
                                    {
                                        dealerId ? (
                                            <Select url={`${contractApi.getContractsDict}?dealerId=${dealerId}`}
                                                    getData={data => data.data} textField={'code'}/>
                                        ) : <AntSelect/>
                                    }
                                </Form.Item>
                            </Col>
                        ) : (null)
                    }
                    <Col span={8} key={'startDate'}>
                        <Form.Item name={'startDate'} label={'开始日期'} rules={[{required: true}]}
                                   initialValue={dayjs()}>
                            <DatePicker/>
                        </Form.Item>
                    </Col>
                    <Col span={8} key={'endDate'}>
                        <Form.Item name={'endDate'} label={'结束日期'} rules={[{required: true}]}
                                   initialValue={dayjs('2099-12-31')}>
                            <DatePicker
                                disabled={statusMode !== StatusMode.EnableModifyDate && statusMode !== StatusMode.EnableModifyAll}/>
                        </Form.Item>
                    </Col>
                    <Col span={24} key={'remark'}>
                        <Form.Item name={'remark'} label={'备注'} labelCol={{span: 2}}>
                            <Input.TextArea rows={3}/>
                        </Form.Item>
                    </Col>
                </Row>
                {
                    <>
                        {(businessScope == RebateBusinessScopeEnum.Product || businessScope == RebateBusinessScopeEnum.Category) &&
                            <h3 className={styles.SectionTitle}>业务范围</h3>}
                        {businessScope == RebateBusinessScopeEnum.Product && <Form.List name={'products'}>
                            {fields => <Table key={'product-table'} columns={productColumns}
                                              dataSource={products?.length ? form.getFieldValue('products') : [{}]}
                                              pagination={false}/>}
                        </Form.List>}
                        {businessScope == RebateBusinessScopeEnum.Category && <Form.List name={'categories'}>
                            {fields => <Table key={'product-table'} columns={categoryColumns}
                                              dataSource={categories?.length ? form.getFieldValue('categories') : [{}]}
                                              pagination={false}/>}
                        </Form.List>}
                    </>
                }
            </Form>
            <ProductSelectModal multiple
                                key={'product-select-modal'}
                                open={productSelectedModalVisible}
                                onCancel={() => setProductSelectedModalVisible(false)}
                                queryParams={{status: true}}
                                onComplete={handleProductSelected}/>
            <ProductSelectModal multiple
                                key={'category-select-modal'}
                                open={categorySelectedModalVisible}
                                columns={categorySelectColumns}
                                onCancel={() => setCategorySelectedModalVisible(false)}
                                url={categoryApi.getCategories}
                                searchColumns={[]}
                                onComplete={handleCategoriesSelected}/>
        </Card>
        <UploadModal onUpload={file => doImport(id!!, file)}
                     open={uploadModalVisible}
                     title={'导入返利详情'}
                     template={'/template/返利详情导入模版.xlsx'}
                     confirmLoading={doImportLoading}
                     onCancel={() => setUploadModalVisible(false)}/>
    </PageHeaderLayout>
}

export default RebateDetail;
